import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronsLeft, Moon, Sun, X } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import LanguageSwitch from '../LanguageSwitch/LanguageSwitch';
import { useIsCompact, useSidebarOpen, useTheme } from '../Layout/shellState';
import './Sidebar.css';

/** Square brand mark: yellow tile with the "d" and sun rays (platform BrandMark). */
export function BrandMark({ size = 40 }) {
  return (
    <span className="brand-mark" style={{ width: size, height: size }} aria-hidden="true">
      <svg viewBox="0 0 40 40" width={size * 0.7} height={size * 0.7} fill="none">
        <path d="M22 6 L22 11" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
        <path d="M13.5 8.5 L16 13" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
        <path d="M30.5 8.5 L28 13" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
        <path
          d="M26.5 14 V33 M26.5 24.5 C26.5 29 23.5 32 19.5 32 C15.5 32 12.5 29 12.5 24.5 C12.5 20 15.5 17 19.5 17 C23.5 17 26.5 20 26.5 24.5 Z"
          stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

/*
 * The wordmark as vector paths, the same drawing the platform ships (components/shell/BrandLogo).
 * The letters take the text colour, so they are ink on light and white on dark; the five sun
 * rays stay brand yellow in both themes. (It used to be a PNG flattened to white in dark mode
 * by a CSS filter, which turned the yellow rays white too.) No image request, sharp at any size.
 */
const LOGO_W = 1075;
const LOGO_H = 512;
const SUN = '#FFC107';
const INK =
  'M873 512L929 402L845.5 219L899.5 219L957.5 342L1015.5 219L1075 219L933 512ZM661 411c-12-1-25-5-36-10c-20-9-33-27-37-49l0-4 26 0l25 0 1 4c1 6 4 10 8 15c8 6 14 8 27 8c9 0 11 0 16-2c4-2 7-5 9-7c3-4 4-11 1-16c-3-8-10-11-45-20c-27-6-36-9-47-17c-15-10-22-27-18-47c4-21 23-39 50-46c8-2 11-2 26-2c15 0 17 0 26 2c25 7 41 21 51 42c3 6 6 15 6 19l0 2-24 0l-24-1-2-5c-6-17-22-25-42-22c-13 2-20 9-20 19c0 12 8 17 42 24c34 8 51 15 62 28c10 11 14 25 11 41c-3 17-18 32-38 39c-14 5-38 7-54 5zM291 408c-18-3-36-13-48-26c-27-29-33-76-13-114c5-10 8-15 16-23c23-25 60-34 92-24c10 4 19 9 28 18l8 8 0-14l0-14 25 0l25 0 0 94l0 93-25 1l-25 0 0-17l0-17-4 5c-4 6-16 17-22 21c-7 4-19 8-27 9c-8 1-22 1-30 0zM66 406c-23-6-43-22-56-45c-14-26-13-67 2-95c13-24 33-40 58-46c8-2 11-2 21-2c19 1 34 7 46 19c4 3 7 6 7 6c1 0 1-21 1-47l0-46 26 0l25 0 1 129l0 128-25 0l-24-1-1-16l0-17-3 5c-9 13-23 23-41 28c-11 3-26 3-37 0zM454 406c0-1 0-43 0-94l0-92 27-1l27 0 0 20l0 19 2-3c5-6 7-7 15-14c8-8 18-14 27-18c6-2 20-6 24-6l3 0 0 27l-1 26-4 0c-6 0-23 2-30 4c-15 5-28 14-33 24l-3 6 0 51l0 52-27 0c-20 0-26 0-27-1zM781 279l1-129 26 0l26 0 0 129l0 128-26 0l-27 0 0-128zM333 366c8-2 19-7 25-13c7-7 10-12 13-20c2-6 2-8 2-20c0-13 0-14-3-22c-5-13-17-24-31-29c-18-5-40 0-53 13c-11 11-16 23-16 40c0 11 2 17 6 25c3 7 14 18 21 21c9 5 24 7 36 5zM121 361c9-5 16-12 21-21c5-9 6-15 6-27c0-17-4-29-13-38c-18-19-49-19-69 0c-7 7-10 13-13 24c-5 19 0 39 13 52c11 10 21 14 37 14c9-1 11-1 18-4z';
// The five sun rays: round-capped strokes, [path, stroke width].
const RAYS = [
  ['M165.1 18.6L165.1 106.4', 37.1],
  ['M60.9 60.3L118 117.8', 35.2],
  ['M279.2 60.5L221.9 117.7', 35.3],
  ['M105.9 162.9L20.1 163', 34.2],
  ['M327.1 162.6L233.9 162.9', 33.7],
];

/**
 * Darsly wordmark. `decorative` hides it from screen readers (use it inside a link that already
 * carries the name "Darsly CRM").
 */
export function BrandLogo({ height = 30, className = '', decorative = false }) {
  const width = Math.round((height * LOGO_W) / LOGO_H);
  return (
    <svg
      viewBox={`0 0 ${LOGO_W} ${LOGO_H}`}
      width={width}
      height={height}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : 'Darsly'}
      aria-hidden={decorative || undefined}
      focusable="false"
      className={`brand-logo ${className}`.trim()}
    >
      <g fill="none" stroke={SUN} strokeLinecap="round">
        {RAYS.map(([d, w]) => (
          <path key={d} d={d} strokeWidth={w} />
        ))}
      </g>
      <path d={INK} fill="currentColor" />
    </svg>
  );
}

export function initialsOf(name) {
  return (name || 'U')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'U';
}

/**
 * The signed-in staff member's picture, or yellow initials when there is none (or it does not
 * load). Sits beside the name, so it is hidden from screen readers.
 */
export function ShellAvatar({ name, src, size = 36, presence = false, className = '' }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);
  const url = typeof src === 'string' && src && !failed ? src : null;
  return (
    <span
      className={`sidebar-avatar ${className}`.trim()}
      style={{ '--avatar-size': `${size}px` }}
      aria-hidden="true"
    >
      {url
        ? <img src={url} alt="" referrerPolicy="no-referrer" onError={() => setFailed(true)} />
        : <span className="sidebar-avatar-initials">{initialsOf(name)}</span>}
      {presence && <span className="sidebar-avatar-dot" />}
    </span>
  );
}

function SidebarItem({ item, collapsed, onNavigate }) {
  const { Icon, badge } = item;
  return (
    <Link
      to={item.to}
      className={`sidebar-item${item.active ? ' active' : ''}`}
      aria-current={item.active ? 'page' : undefined}
      title={collapsed ? item.label : undefined}
      onClick={onNavigate}
    >
      <span className="sidebar-icon" aria-hidden="true">
        <Icon strokeWidth={item.active ? 2.4 : 2} aria-hidden="true" focusable="false" />
      </span>
      <span className="sidebar-label">{item.label}</span>
      {badge ? (
        <span
          className="sidebar-badge ui-count"
          title={badge.label}
          role="status"
          aria-label={badge.label}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); badge.onActivate?.(); }}
        >
          {badge.text}
        </span>
      ) : null}
    </Link>
  );
}

/** Theme and language for the phone drawer (the top pill has no room for them): one row. */
function DrawerTools() {
  const { t } = useI18n();
  const { isDark, setTheme } = useTheme();
  return (
    <div className="sidebar-tools">
      <div className="sidebar-seg sidebar-seg--theme" role="group" aria-label={t('topbar.theme')}>
        <button
          type="button"
          className={`sidebar-seg-btn${isDark ? '' : ' is-active'}`}
          aria-pressed={!isDark}
          aria-label={t('layout.themeLight')}
          title={t('layout.themeLight')}
          onClick={() => setTheme('light')}
        >
          <Sun size={18} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={`sidebar-seg-btn${isDark ? ' is-active' : ''}`}
          aria-pressed={isDark}
          aria-label={t('layout.themeDark')}
          title={t('layout.themeDark')}
          onClick={() => setTheme('dark')}
        >
          <Moon size={18} aria-hidden="true" />
        </button>
      </div>
      <LanguageSwitch className="sidebar-seg sidebar-seg--lang" buttonClassName="sidebar-seg-btn" />
    </div>
  );
}

/**
 * True while the rail's navigation has more below the fold (a short laptop screen): the list then
 * fades out at the bottom, so a hidden last destination is never a surprise.
 */
function useMoreBelow(ref) {
  const [more, setMore] = useState(false);
  const measure = useCallback(() => {
    const el = ref.current;
    if (el) setMore(el.scrollHeight - el.clientHeight - el.scrollTop > 2);
  }, [ref]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [ref, measure]);
  // The list changes with the role and the language: look again after every render.
  useEffect(measure);
  return more;
}

/**
 * The rail's chrome, shared by the staff and teacher shells: brand row with the round toggle,
 * grouped navigation, and the user card. On desktop it is a floating glass rail (open, or an icon
 * rail); below 1024px the very same element is the drawer the top pill's menu button opens.
 *
 * groups: [{ key, label, items: [{ key, to, label, Icon, active, badge? }] }]
 * user:   { name, sub, avatar, to }
 * tag:    { label, tone } — the small pill beside the logo
 */
function SidebarFrame({
  className = '', ariaLabel, homeTo, tag, groups, user, mobileOpen = false, onMobileClose,
}) {
  const { t } = useI18n();
  const [open, setOpen] = useSidebarOpen();
  const compact = useIsCompact();
  const collapsed = !open && !compact;
  const navRef = useRef(null);
  const moreBelow = useMoreBelow(navRef);

  const classes = ['sidebar', className, open ? '' : 'is-collapsed', mobileOpen ? 'is-mobile-open' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <aside className={classes} aria-label={ariaLabel}>
      <div className="sidebar-brand">
        <button
          type="button"
          className="sidebar-toggle"
          onClick={() => setOpen(!open)}
          aria-label={open ? t('nav.collapse') : t('nav.expand')}
          aria-expanded={open}
          title={open ? t('nav.collapse') : t('nav.expand')}
        >
          <ChevronsLeft size={16} aria-hidden="true" />
        </button>
        <Link to={homeTo} className="sidebar-brand-link" aria-label="Darsly CRM" onClick={onMobileClose}>
          <BrandLogo height={30} decorative />
        </Link>
        {tag && (
          <span className={`ds-badge ds-badge-${tag.tone || 'neutral'} sidebar-tag`}>{tag.label}</span>
        )}
        <button
          type="button"
          className="sidebar-mobile-close"
          onClick={onMobileClose}
          aria-label={t('ui.close')}
        >
          <X size={20} aria-hidden="true" />
        </button>
      </div>

      <nav ref={navRef} className={`sidebar-nav${moreBelow ? ' has-more-below' : ''}`}>
        {groups.map((group) => (
          <div className="sidebar-group" key={group.key}>
            {/* One fixed-height slot for both states: the group's name when the rail is open,
                a short hairline on the icon column when it is closed. */}
            <div className="sidebar-group-head">
              <p className="sidebar-section-title" aria-hidden={collapsed ? 'true' : undefined}>{group.label}</p>
              <span className="sidebar-group-rule" aria-hidden="true" />
            </div>
            <div className="sidebar-menu">
              {group.items.map((item) => (
                <SidebarItem key={item.key} item={item} collapsed={collapsed} onNavigate={onMobileClose} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <Link to={user.to} className="sidebar-user" title={user.name} onClick={onMobileClose}>
          <ShellAvatar name={user.name} src={user.avatar} size={36} presence />
          <span className="sidebar-user-meta">
            <span className="sidebar-user-name">{user.name}</span>
            {user.sub ? <span className="sidebar-user-sub">{user.sub}</span> : null}
          </span>
        </Link>
        {compact && <DrawerTools />}
      </div>
    </aside>
  );
}

export default SidebarFrame;
